import { useState, useRef, useEffect } from "react";
import api from "../../api/axios";
import SendIcon from "../../assets/icons/send_button.png";
import MicrophoneIcon from "../../assets/icons/mic.png";
import PlusIcon from "../../assets/icons/plus.svg";
import LogoLargeIcon from "../../assets/icons/center_logo.png";

// Quick action button labels
const QUICK_OPTIONS = [
  "Health concern",
  "Fitness & lifestyle",
  "Doctor consultation"
];

// =========================
// MEDICAL DOCUMENT VALIDATION CONFIG
// =========================

// Allowed MIME types for medical documents
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png"
];

// Allowed file extensions
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

// File size limits
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_FILE_SIZE = 1024; // 1KB minimum

// Medical keywords that should be in filename
const MEDICAL_KEYWORDS = [
  "report", "prescription", "lab", "test", "scan",
  "xray", "x-ray", "mri", "ct", "blood", "medical",
  "diagnosis", "result", "pathology", "radiology",
  "ultrasound", "ecg", "ekg", "thyroid", "lipid",
  "cbc", "hba1c", "urine", "stool", "biopsy"
];

// Check if filename contains medical keywords
const isMedicalFilename = (filename) => {
  const lowerName = filename.toLowerCase();
  return MEDICAL_KEYWORDS.some(keyword => lowerName.includes(keyword));
};

// Validate medical document
const validateMedicalDocument = (file) => {
  if (!file || !file.name) {
    return { valid: false, error: "No file selected." };
  }

  const fileName = file.name.toLowerCase();
  const lastDotIndex = fileName.lastIndexOf(".");
  const fileExtension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";
  const fileMimeType = file.type ? file.type.toLowerCase() : "";

  // Check file extension
  if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      error: "Invalid file format. Please upload PDF, JPG, or PNG files only."
    };
  }

  // Check MIME type
  if (fileMimeType && !ALLOWED_MIME_TYPES.includes(fileMimeType)) {
    return {
      valid: false,
      error: "Invalid file type. Please upload a valid PDF or image file."
    };
  }

  // Check minimum file size (empty/corrupt)
  if (!file.size || file.size < MIN_FILE_SIZE) {
    return {
      valid: false,
      error: "This file appears to be empty or corrupt. Please upload a valid document."
    };
  }

  // Check maximum file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: "File too large. Maximum size is 5MB."
    };
  }

  // Check for medical keywords in filename
  if (!isMedicalFilename(fileName)) {
    return {
      valid: false,
      error: "This document doesn't look like a medical report. Please upload a valid medical document (lab report, prescription, scan report, etc.)."
    };
  }

  return { valid: true, error: null };
};

function ChatWindow({ messages, setMessages, sessionId: propSessionId }) {
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(propSessionId || null);
  const [showAttachPopup, setShowAttachPopup] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({}); // Track selected options per message index
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Handle disclaimer acceptance
  const handleAcceptDisclaimer = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
  };

  // Sync sessionId with prop when chat changes
  useEffect(() => {
    setCurrentSessionId(propSessionId || null);
    setSelectedOptions({});

    const isFreshDraft = !propSessionId || String(propSessionId).startsWith("chat_");
    if (isFreshDraft) {
      // New blank chat — show disclaimer once
      setDisclaimerAccepted(false);
      setShowDisclaimer(true);
    } else {
      // Loaded history / remapped server session — never show disclaimer popup
      setShowDisclaimer(false);
    }
  }, [propSessionId]);

  // Hide as soon as user starts chatting (prevents flash on first message)
  useEffect(() => {
    if (messages.length > 0) {
      setShowDisclaimer(false);
      setDisclaimerAccepted(true);
    }
  }, [messages.length]);

  const isFreshDraft =
    !propSessionId || String(propSessionId).startsWith("chat_");
  // Render guard: never show after first message or on server sessions
  const shouldShowDisclaimer =
    showDisclaimer &&
    !disclaimerAccepted &&
    messages.length === 0 &&
    isFreshDraft;
  const messagesEndRef = useRef(null);
  const attachPopupRef = useRef(null);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (attachPopupRef.current && !attachPopupRef.current.contains(e.target) && !e.target.closest(".attach-btn")) {
        setShowAttachPopup(false);
      }
    };
    if (showAttachPopup) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAttachPopup]);

  const isLanding = messages.length === 0;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Clear upload error after 5 seconds
  useEffect(() => {
    if (uploadError) {
      const timer = setTimeout(() => setUploadError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [uploadError]);

  // Handle image file selection (medical images only)
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = ""; // Reset input
  };

  // Handle document file selection (medical documents only)
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = ""; // Reset input
  };

  // Process and validate medical documents
  const processFiles = (files) => {
    // Clear any previous error
    setUploadError(null);

    for (const file of files) {
      // Validate medical document
      const validation = validateMedicalDocument(file);

      if (!validation.valid) {
        setUploadError(validation.error);
        return; // Stop - reject invalid file
      }

      // All validations passed - create file object
      const isImage = file.type && file.type.startsWith("image/");
      const fileObj = {
        file,
        name: file.name,
        type: isImage ? "image" : "document",
        size: file.size,
        preview: isImage ? URL.createObjectURL(file) : null,
        id: Date.now() + Math.random()
      };

      // Add to attached files
      setAttachedFiles(prev => [...prev, fileObj]);
      setShowAttachPopup(false);
    }
  };

  // Remove attached file
  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Revoke object URL to prevent memory leak
      const removed = prev.find(f => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Get file icon based on type
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const icons = {
      pdf: "📄",
      doc: "📝",
      docx: "📝",
      txt: "📃",
      xls: "📊",
      xlsx: "📊"
    };
    return icons[ext] || "📎";
  };

  const sendMessage = async (text) => {
    const hasText = text.trim().length > 0;
    const hasFiles = attachedFiles.length > 0;

    if ((!hasText && !hasFiles) || isTyping) return;

    // Create user message with attachments
    const userMessage = {
      from: "user",
      text: hasText ? text : "",
      attachments: attachedFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
        preview: f.preview,
        file: f.file
      }))
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setAttachedFiles([]);
    setIsTyping(true);

    try {
      // If there are files, use FormData
      let res;
      if (hasFiles) {
        const formData = new FormData();
        formData.append("message", text || "");
        if (currentSessionId) {
          formData.append("session_id", currentSessionId);
        }

        // Append each file with proper naming
        userMessage.attachments.forEach((attachment, index) => {
          formData.append(`file_${index}`, attachment.file, attachment.name);
        });

        // Don't set Content-Type header - let axios handle it for multipart
        res = await api.post("chat/", formData);
      } else {
        res = await api.post("chat/", {
          message: text,
          session_id: currentSessionId,
        });
      }

      // Get the session ID from response (for new chats)
      const newSessionId = res.data.session_id;

      // Update local session ID
      if (!currentSessionId && newSessionId) {
        setCurrentSessionId(newSessionId);
      }

      // Build final messages
      const finalMessages = [
        ...updatedMessages,
        { from: "bot", text: String(res.data.reply) },
      ];

      // Pass session ID + auto title to parent
      const title = res.data.title || null;
      if (!currentSessionId && newSessionId) {
        setMessages(finalMessages, newSessionId, title);
      } else {
        setMessages(finalMessages, newSessionId || currentSessionId, title);
      }
    } catch (error) {
      // Check if backend returned a validation error
      let errorMessage = "Server error. Please try again.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      setMessages([
        ...updatedMessages,
        { from: "bot", text: errorMessage },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => sendMessage(input);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle quick option button click - sends as user message
  const handleQuickOption = (optionText) => {
    if (isTyping) return;
    sendMessage(optionText);
  };

  // --- Speech-to-text (microphone) support ---
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const keepListeningRef = useRef(false); // whether mic toggle requests continuous listening
  const recordingTimerRef = useRef(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const prevInputRef = useRef('');

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    // indicate we want continuous listening until user toggles mic off
    keepListeningRef.current = true;

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = navigator.language || 'en-US';
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.continuous = true;

      recognition.onstart = () => {
        // preserve current input so new transcripts append rather than replace
        prevInputRef.current = input || prevInputRef.current || '';
        setIsListening(true);
        setRecordingSeconds(0);
        // ensure no duplicate timers
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
      };

      recognition.onresult = (event) => {
        let interim = '';
        let final = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) final += transcript;
          else interim += transcript;
        }

        // Build base from previously preserved input (may be updated by typing while listening)
        let base = prevInputRef.current || '';

        // If there is a final chunk, commit it immediately so it won't be lost
        if (final && final.trim().length > 0) {
          // Ensure spacing between existing text and appended transcript
          const needsSpace = base.length > 0 && !/\s$/.test(base);
          base = base + (needsSpace ? ' ' : '') + final.trim();
          // Commit the final text to prevInputRef so subsequent interim results append correctly
          prevInputRef.current = base;
        }

        // Show interim result after the committed finals (do not overwrite committed text)
        const interimText = interim ? ((base.length > 0 && !/\s$/.test(base) ? ' ' : '') + interim) : '';
        setInput(base + interimText);
      };

      recognition.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'permission-denied') {
          alert('Microphone permission denied. Please allow microphone access to use voice input.');
        } else {
          console.error('Speech recognition error', e);
        }
        stopListening();
      };

      recognition.onend = () => {
        // Stop timer always
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        // If the user still wants to keep listening (didn't toggle mic off), restart recognition.
        if (keepListeningRef.current) {
          // small delay to avoid tight restart loops
          setTimeout(() => {
            if (keepListeningRef.current) {
              try { startListening(); } catch (e) { console.error('Restart recognition failed', e); }
            }
          }, 250);
          return;
        }

        // final stop: keep text in input but do NOT auto-send
        setIsListening(false);
        setRecordingSeconds(0);
        // Update prevInputRef to current input so subsequent listens append correctly
        prevInputRef.current = input || '';
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Unable to start speech recognition', err);
      alert('Unable to access microphone. Please check microphone permissions.');
    }
  };

  const stopListening = () => {
    // user intentionally turned mic off
    keepListeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch (e) { console.error(e); }
    try { recognitionRef.current = null; } catch (e) { }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsListening(false);
    setRecordingSeconds(0);
  };

  const toggleListening = () => {
    if (isListening) stopListening(); else startListening();
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.abort(); } catch (e) { }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  // Check if text contains clickable options (bullet points)
  // Only convert to buttons if it's the "How can I help you" message and buttons haven't been used
  const parseOptionsFromText = (text) => {
    if (!text) return { hasOptions: false, headerText: "", options: [] };

    const lowerText = text.toLowerCase();

    // Skip certain messages that should NOT have buttons (e.g., multi-step instructions)
    const skipPatterns = [
      "follow these",
      "instructions:",
      "steps:",
    ];
    // Only skip when message looks like long instructions; allow short tip lists to be converted into buttons
    if (skipPatterns.some(pattern => lowerText.includes(pattern))) {
      return { hasOptions: false, headerText: "", options: [] };
    }

    // Pattern 1: Inline bullets like "• Option1 • Option2 • Option3"
    const inlineBulletPattern = /•\s*([^•\n]+)/g;
    const inlineMatches = [...text.matchAll(inlineBulletPattern)];

    if (inlineMatches.length >= 2) {
      // Check if bullets are on the same line (inline)
      const firstBulletIndex = text.indexOf('•');
      const headerText = firstBulletIndex > 0 ? text.substring(0, firstBulletIndex).trim() : "";

      const options = inlineMatches
        .map(m => m[1].trim())
        .filter(opt => opt.length > 0 && opt.length < 50) // Filter out very long text
        .filter(opt => !opt.toLowerCase().includes("medical record"));

      if (options.length >= 2) {
        return { hasOptions: true, headerText, options };
      }
    }

    // Pattern 2: Newline bullets like:
    // • Option1
    // • Option2
    const newlineBulletRegex = /^[\s]*[•\-]\s*(.+)$/gm;
    const newlineMatches = [...text.matchAll(newlineBulletRegex)];

    if (newlineMatches.length >= 2) {
      const firstBulletIndex = text.search(/[\n\r][\s]*[•\-]/);
      const headerText = firstBulletIndex > 0 ? text.substring(0, firstBulletIndex).trim() : "";

      const options = newlineMatches
        .map(m => m[1].trim())
        .filter(opt => opt.length > 0 && opt.length < 50)
        .filter(opt => !opt.toLowerCase().includes("medical record"));

      if (options.length >= 2) {
        return { hasOptions: true, headerText, options };
      }
    }

    return { hasOptions: false, headerText: "", options: [] };
  };

  // Convert URLs in text to clickable links, and bullet points to buttons for bot messages
  const renderMessage = (text, isBot = false, messageIndex = -1) => {
    if (!text) return null;

    // Check for clickable options in bot messages
    if (isBot) {
      const { hasOptions, headerText, options } = parseOptionsFromText(text);

      if (hasOptions && options.length > 0) {
        const selectedOption = selectedOptions[messageIndex];

        return (
          <div>
            {headerText && <div style={{ marginBottom: "12px" }}>{headerText}</div>}
            <div style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "8px"
            }}>
              {options.map((option, idx) => {
                const isSelected = selectedOption === option;

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setSelectedOptions(prev => ({ ...prev, [messageIndex]: option }));
                      handleQuickOption(option);
                    }}
                    disabled={isTyping}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "18px",
                      border: "1px solid #0f52ba",
                      background: isSelected ? "#0f52ba" : "transparent",
                      color: isSelected ? "#fff" : "#0f52ba",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: isTyping ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                      opacity: isTyping ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isTyping && !isSelected) {
                        e.target.style.background = "#0f52ba";
                        e.target.style.color = "#fff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#0f52ba";
                      }
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
    }

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#007bff", textDecoration: "underline" }}
          >
            {part}
          </a>
        );
      }
      // Preserve line breaks
      return part.split("\n").map((line, lineIndex, array) => (
        <span key={`${index}-${lineIndex}`}>
          {line}
          {lineIndex < array.length - 1 && <br />}
        </span>
      ));
    });
  };

  // Render message attachments
  const renderAttachments = (attachments, isUser) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="message-attachments">
        {attachments.map((attachment, index) => (
          <div key={index} className={`attachment-item ${attachment.type}`}>
            {attachment.type === "image" && attachment.preview ? (
              <div className="attachment-image-container">
                <img
                  src={attachment.preview}
                  alt={attachment.name}
                  className="attachment-image"
                  onClick={() => window.open(attachment.preview, '_blank')}
                />
              </div>
            ) : (
              <div className={`attachment-document ${isUser ? 'user-doc' : 'bot-doc'}`}>
                <span className="doc-icon">{getFileIcon(attachment.name)}</span>
                <div className="doc-info">
                  <span className="doc-name">{attachment.name}</span>
                  <span className="doc-size">{formatFileSize(attachment.size)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="chat-area">
      {/* ================= DISCLAIMER POPUP ================= */}
      {shouldShowDisclaimer && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: "32px",
            maxWidth: "450px",
            margin: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              backgroundColor: "#EBF5FF",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}>
              <span style={{ fontSize: "28px" }}>⚕️</span>
            </div>

            <h2 style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "16px",
            }}>
              Important Disclaimer
            </h2>

            <p style={{
              fontSize: "14px",
              color: "#666",
              lineHeight: "1.6",
              marginBottom: "24px",
            }}>
              Fitnova chatbot provides general guidance based on user inputs and available medical records.
              It does not diagnose or prescribe treatment. For medical decisions, please consult a qualified doctor.
            </p>

            <button
              type="button"
              onClick={handleAcceptDisclaimer}
              style={{
                width: "100%",
                padding: "14px 24px",
                backgroundColor: "#0f52ba",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#2563EB"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#0f52ba"}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* ================= LANDING VIEW ================= */}
      {isLanding ? (
        <div className="chat-landing">
          <img src={LogoLargeIcon} alt="Fitnova" className="landing-logo" />

          <h1>Hello, How can I help you?</h1>
          <p>
            I'm Fitnova, your mental wellness companion.
            Ask me anything or share how you're feeling.
          </p>
        </div>
      ) : (
        /* ================= CHAT VIEW ================= */
        <div className="chat-messages">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`message ${msg.from === "user" ? "user" : "bot"} ${msg.attachments?.length ? 'has-attachments' : ''}`}
            >
              {renderAttachments(msg.attachments, msg.from === "user")}
              {msg.text && renderMessage(msg.text, msg.from === "bot", index)}
            </div>
          ))}

          {isTyping && (
            <div className="message bot typing">
              Fitnova is typing…
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* ================= INPUT (ALWAYS VISIBLE) ================= */}
      <div className="chat-input-wrapper">
        {/* Hidden file inputs */}
        <input
          type="file"
          ref={imageInputRef}
          onChange={handleImageSelect}
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          style={{ display: "none" }}
        />
        <input
          type="file"
          ref={documentInputRef}
          onChange={handleDocumentSelect}
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
        />

        {/* Upload error message */}
        {uploadError && (
          <div className="upload-error">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="error-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#EF4444" />
            </svg>
            <span>{uploadError}</span>
            <button onClick={() => setUploadError(null)} className="error-close">×</button>
          </div>
        )}

        {/* Attached files preview */}
        {attachedFiles.length > 0 && (
          <div className="attached-files-preview">
            {attachedFiles.map((file) => (
              <div key={file.id} className="preview-item">
                {file.type === "image" ? (
                  <img src={file.preview} alt={file.name} className="preview-thumbnail" />
                ) : (
                  <div className="preview-doc">
                    <span className="preview-doc-icon">{getFileIcon(file.name)}</span>
                  </div>
                )}
                <div className="preview-info">
                  <span className="preview-name" title={file.name}>
                    {file.name.length > 15 ? file.name.substring(0, 12) + "..." : file.name}
                  </span>
                  <span className="preview-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  className="preview-remove"
                  onClick={() => removeAttachedFile(file.id)}
                  aria-label="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {showAttachPopup && (
          <div className="attach-popup" ref={attachPopupRef}>
            <button
              type="button"
              className="attach-popup-btn"
              onClick={() => {
                imageInputRef.current?.click();
                setShowAttachPopup(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#0f52ba" />
              </svg>
              Image
            </button>
            <button
              type="button"
              className="attach-popup-btn"
              onClick={() => {
                documentInputRef.current?.click();
                setShowAttachPopup(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#0f52ba" />
              </svg>
              Document
            </button>
          </div>
        )}
        <div className="chat-input-container">
          <div className="chat-input-box">
            <button
              type="button"
              className="attach-btn"
              onClick={() => setShowAttachPopup((prev) => !prev)}
              aria-expanded={showAttachPopup}
              aria-label="Attach file"
            >
              <img src={PlusIcon} alt="" />
            </button>

            <input
              placeholder="Chat with Fitnova..."
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // if listening, update the base text to avoid overwriting user's manual edits
                if (isListening) prevInputRef.current = e.target.value;
              }}
              onKeyDown={handleKeyDown}
              disabled={isTyping}
            />

            <div className="input-right-icons">
              <button
                type="button"
                className={`mic-btn ${isListening ? 'recording' : ''}`}
                aria-label="Voice input"
                aria-pressed={isListening}
                onClick={toggleListening}
                title={isListening ? `Listening… (${recordingSeconds}s) — click to stop` : 'Click and speak'}
                style={isListening ? { background: '#F3F4F6', transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.12)' } : undefined}
              >
                <img src={MicrophoneIcon} alt="" />
              </button>
              {isListening && (
                <div style={{ fontSize: 12, color: '#0f52ba', marginRight: 8, minWidth: 36, textAlign: 'center' }}>
                  ● {recordingSeconds}s
                </div>
              )}
              <button
                type="button"
                className="send-btn"
                onClick={handleSend}
                disabled={isTyping || (!input.trim() && attachedFiles.length === 0)}
                aria-label="Send"
              >
                <img src={SendIcon} alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;